import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';

import { ProfileType } from '../../../models/profiletype.model';
import { NotificationService } from '../../../services/notificationservice';
import { Regions } from '../../../interfaces/regions';
import { LookUpService } from '../../../services/lookupservice';
import { MatIcon } from '@angular/material/icon';
import { HaulerPricingDialogComponent } from '../Hauler-Pricing-dilaog/hauler-pricing-dialog.component';
import { HaulerService } from '../../../services/haulerservice';
import { States } from '../../../interfaces/states';
import { SiteService } from '../../../services/siteservice';
import { Cities } from '../../../interfaces/cities';
import { MatDialogModule } from '@angular/material/dialog';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { FeedstockService } from '../../../services/feedstockservice';
import { SchedulingSitesResponse } from '../../../interfaces/siteType';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-addedit-hauler',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTableModule,
    MatMenuModule,
    MatIcon,
    MatDialogModule,
    DragDropModule
  ],
  providers: [
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} },
  ],
  templateUrl: './addedit-hauler.component.html',
  styleUrl: './addedit-hauler.component.scss',
})
export class AddeditHaulerComponent implements OnInit {
  @Input() haulerData: any;
  @Output('onFormSubmit') formEmitter = new EventEmitter<any>();

  profile: ProfileType | undefined;
  haulerForm!: FormGroup;
  isEditMode = false;
  markets: Regions[] = [];
  filteredMarkets: Regions[] = [];
  states: States[] = [];
  filteredStates: States[] = [];
  cities: Cities[] = [];
  filteredCities: Cities[] = [];
  marketId: string | null = null;
  haulerDetails: any;
  canSave: boolean = false;
  data = {
    id: '',
    name: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    marketId: '',
  };

  priceMatrixColumns: string[] = [
    'actions',
    'uom',
    'price',
    'fuel_Surcharge',
    'demurag_Rate',
    'disposalSite',
    'streamId',
    // 'cacheID',
  ];
  dataSource = new MatTableDataSource<any>();
  disposalSites = signal<SchedulingSitesResponse[]>([]);
  constructor(private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddeditHaulerComponent>, private _dialog: MatDialog,
    private lookUp: LookUpService, private siteService: SiteService,
    private haulerService: HaulerService, private notificationService: NotificationService,
    private fsService: FeedstockService
  ) {}

  ngOnInit(): void {
    this.canSave = this.haulerData.canSave;
    if (this.haulerData?.formData) {
      this.data = this.haulerData.formData;
      this.isEditMode = !this.haulerData.isAddForm;
    }

    this.buildForm();
    this.getMarketsData();
    this.getStatesData();
    if (this.data.id != null) {
      this.getHaulerDetailsById(this.data.id);
    }
  }

  buildForm(): void {
    console.log('Received hauler data for editing:', this.data);
    this.haulerForm = this.fb.group({
      name: [this.data.name, Validators.required],
      contactName: [this.data.contactName, Validators.required],
      contactPhone: [
        this.data.contactPhone,
        [Validators.required, Validators.pattern(/^(\+1[- ]?)?(\([0-9]{3}\)|[0-9]{3})[- ]?[0-9]{3}[- ]?[0-9]{4}$/),],
      ],
      contactEmail: [
        this.data.contactEmail,
        [Validators.required, Validators.email],
      ],
      addressLine1: [this.data.addressLine1, Validators.required],
      addressLine2: [this.data.addressLine2],
      city: [this.data.city, Validators.required],
      state: [this.data.state, Validators.required],
      zipCode: [this.data.zipCode, Validators.required],
      marketId: [this.data.marketId, Validators.required],
    });
  }

  onSubmit(): void {
    this.haulerForm.markAllAsTouched();
    
    if (this.haulerForm.valid) {
      const rawValues = this.haulerForm.getRawValue();
      const selectedStateId = rawValues.state;
      const stateObj = this.states.find(s => s.id === selectedStateId);
      const stateName = stateObj ? stateObj.state : '';
      const formData = { 
        ...rawValues,
        state: stateName, // Overwrite the ID with the Name
        ID: this.data.id || 0,
        haulerPriceRequests: this.handleHaulerSubmit()
      };
      console.log('Form Data before API call:', formData);
      
      this.haulerService.createHauler(formData).subscribe({
        next: () => {
          this.formEmitter.emit({ submit: true });
          this.notificationService.show(
            this.isEditMode
              ? 'Hauler updated successfully'
              : 'Hauler created successfully',
            'success'
          );
        }
      });
    }
  }

  getStatesData(): void {
    this.lookUp.getStatesList().subscribe({
      next: (response: States[]) => {
        this.states = response;
        this.filteredStates = response;
        if (this.data.state) {
          const selectedState = this.states.find(
            (s) => s.state === this.data.state
          );
          if (selectedState?.id) {
            this.onStateChanged(selectedState.id, true);
          }
        }
      },
    });
  }

  onStateChanged(selectedState: number, isOnLoad?: boolean): void {
    if (selectedState) {
      this.siteService.getCitiesByState(selectedState).subscribe({
        next: (response: Cities[]) => {
          this.cities = response; // for example
          this.filteredCities = response;
          if (isOnLoad && this.data.city) {
            const selectedCity = this.cities.find(
              (c) => c.city?.toLowerCase() === this.data?.city?.toLowerCase()
            );
            if (selectedCity) {
              this.updateCityAndStateSelection(
                selectedCity.city,
                selectedState
                // this.data.state
              );
            }
          }
        },
      });
    }else{
      this.cities = [];
      this.filteredCities = [];
      this.updateCityAndStateSelection('', '');
    }
  }
  formatPhoneNumber(event: Event, type: number): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    let formattedValue = '';
    if (value.length > 0) {
      formattedValue = '(' + value.substring(0, 3);
    }
    if (value.length >= 4) {
      formattedValue += ') ' + value.substring(3, 6);
    }
    if (value.length >= 7) {
      formattedValue += '-' + value.substring(6, 10);
    }
    input.value = formattedValue;
    if(type === 1){
      this.haulerForm.patchValue({ contactPhone: formattedValue });
    }
    input.value = formattedValue;
  }
  updateCityAndStateSelection(city: string, state: any): void {
    this.haulerForm.patchValue({ city: city });
    this.haulerForm.patchValue({ state: state });
    this.haulerForm.markAsDirty();
  }

  onCancel(): void {
    this.formEmitter.emit({ submit: false });
  }

  getMarketsData(): void {
    this.lookUp.getMarketsList('').subscribe({
      next: (response: Regions[]) => {
        this.markets = response;
        this.filteredMarkets = response;
      }
    });
    this.fsService.getSchedulingSites(1,1).subscribe({
      next: async (response) => {
        console.log("disposal sites response", response);
        this.disposalSites.set(response);
        
      }
    });
  }
  openContextmenu(data: any, action: any) {
    if (action == 'EDIT') {
      this.handleAddPricingDialog(false, data);
    } else if (action == 'DELETE') {
      console.log('delte record ', data);
      const dialogData: ConfirmationDialogData = {
        title: 'Confirm Deletion',
        message: `Are you sure you want to delete Hauler Pricing ${data.streamName}?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'warn'
      };
      
      this._dialog.open(ConfirmationDialogComponent, {
        width: '400px',
        panelClass: 'delete-confirmation-dialog',
        data: dialogData,
        disableClose: true,
      }).afterClosed().subscribe(result => {
        if (result) {
          this.deletePricingRecord(data);
        }
      });
    }
  }

  deletePricingRecord(data: any) {
    let index = -1;

    if (data.cacheID) {
      index = this.dataSource.data.findIndex(
        (item) => item.cacheID === data.cacheID
      );
    } else if (data.id !== undefined && data.id != null && data.id != 0) {
      index = this.dataSource.data.findIndex((item) => item.id === data.id);
    }
    if (index !== -1) {
      this.dataSource.data.splice(index, 1);
      this.refreshGrid();
      this.notificationService.show(
        'Hauler Pricing deleted successfully',
        'success'
      );
    } else {
      this.notificationService.show('Hauler Pricing not found', 'error');
    }
  }

  /** PRICING DETAILS HANDLE */
  handleAddPricingDialog(isAdd?: any, formData?: any): void {
    const dialogRef = this._dialog.open(HaulerPricingDialogComponent, {
      width: '650px',
      disableClose:true,
      data: {
        title: isAdd ? 'Add Hauler Pricing' : 'Edit Hauler Pricing',
        formData: isAdd ? {} : formData || {},
        isAddForm: isAdd || false,
        canSave: this.canSave
      }, // Pass your input data object here
    });

    dialogRef.afterClosed().subscribe((result) => {
      // if (result?.submitted) {
      //   this.dataSource.data = result.data;
      // }
      console.log('The dialog was closed');
      if (result?.submitted && result.data) {
        console.log('Data received:', result.data);
        this.handlehaulerPricingFormSubmit(result.data);
      } else {
        console.log('Dialog cancelled or no data returned.');
      }
    });
  }
  /** END PRICING DETIALS HANDLE */
  handlehaulerPricingFormSubmit(data: any) {
    console.log('handlehaulerPricingFormSubmit', data);
    if (
      (data.id !== undefined && data.id != null && data.id != 0) ||
      (data.cacheID !== undefined && data.cacheID != null && data.cacheID != 0)
    ) {
      // If ID exists, update the existing record
      // Update existing record
      let index = -1;
      if (data.cacheID) {
        index = this.dataSource.data.findIndex(
          (item) => item.cacheID === data.cacheID
        );
      } else {
        index = this.dataSource.data.findIndex((item) => item.id === data.id);
      }
      if (index !== -1) {
        this.dataSource.data[index] = data;
        this.refreshGrid();
        this.notificationService.show(
          'Hauler Pricing updated successfully',
          'success'
        );
      } else {
        this.notificationService.show('Hauler Pricing not found', 'error');
      }
    } else {
      data.cacheID = this.generateCacheID(); // Generate a unique cache ID
      this.dataSource.data.push(data);
      this.refreshGrid();
      this.notificationService.show(
        'Hauler Pricing added successfully',
        'success'
      );
    }
  }
  generateCacheID(): string {
    const timestamp = new Date().getTime();
    const randomNum = Math.floor(Math.random() * 1000);
    return `CACHE-${timestamp}-${randomNum}`;
  }

  refreshGrid(): void {
    this.dataSource._updateChangeSubscription(); // Update the data source
    this.dataSource.data = [...this.dataSource.data];
  }
  handleHaulerSubmit(): void {
    //API CALL FOR ADD OR UPDATE RECORDS
    let records = JSON.parse(JSON.stringify(this.dataSource.data));

    records?.forEach((record: any) => {
      if(!record.disposalSiteids){
        this.disposalSites().forEach(site => {
          if(record.disposalSite && record.disposalSite.includes(site.siteName)){
            record.disposalSiteids = record.disposalSiteids == null ?site.sid : record.disposalSiteids + ',' + site.sid;
          }
        });
      }
      record.disposalSite = record.disposalSiteids;
      record.haulerID = this.data.id || 0;
      delete record.disposalSiteids;
      delete record.cacheID;
      delete record.uomName;
      delete record.generatorName;
      delete record.streamName;
      //record.price = Number(record.price);
      //record.fuel_Surcharge = Number(record.fuel_Surcharge);
      //record.demurag_Rate = Number(record.demurag_Rate);
    });

    return records;
  }

  getHaulerDetailsById(id: any): void {
    this.haulerService.getHaulerDetailsById(id).subscribe({
      next: (response: Regions[]) => {
        this.haulerDetails = response;
        console.log('Hauler details fetched successfully:', this.haulerDetails);
        if (this.haulerDetails) {
          this.haulerForm.patchValue({
            id: this.haulerDetails.orHauler.id,
            name: this.haulerDetails.orHauler.name,
            contactName: this.haulerDetails.orHauler.contactName,
            contactPhone: this.haulerDetails.orHauler.contactPhone,
            contactEmail: this.haulerDetails.orHauler.contactEmail,
            addressLine1: this.haulerDetails.orHauler.addressLine1,
            addressLine2: this.haulerDetails.orHauler.addressLine2,
            city: this.haulerDetails.orHauler.city,
            state: this.haulerDetails.orHauler.state,
            zipCode: this.haulerDetails.orHauler.zipCode,
            marketId: this.haulerDetails.orHauler.marketId
          });
          const stateMatch = this.states.find(s => s.state === this.haulerDetails.orHauler.state);
          if (stateMatch) {
            this.haulerForm.patchValue({ state: stateMatch.id });
            this.onStateChanged(stateMatch.id, true);
          }
          this.dataSource.data = this.haulerDetails.haulerPricing || [];
          this.refreshGrid();
        }
      }
    });
  }

  filterState(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredStates = [...this.states];
    } else {
      this.filteredStates = this.states.filter(
        state => state.state.toLowerCase().includes(searchTerm)
      );
    }
  }

  filterCity(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredCities = [...this.cities];
    } else {
      this.filteredCities = this.cities.filter(
        city => city.city.toLowerCase().includes(searchTerm)
      );
    }
  }

  filterMarket(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    if (!searchTerm) {
      this.filteredMarkets = [...this.markets];
    } else {
      this.filteredMarkets = this.markets.filter(
        market => market.regionName.toLowerCase().includes(searchTerm)
      );
    }
  }
}
